import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { isFieldLockable, isHeaderOrFooterField } from '@/helpers/constants/lockableFields';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { generateHTML } from '@tiptap/html';

const tiptapFocusModule = () => {
  const tiptap = ref({});
  const focusId = ref('');
  const lastFocusId = ref('');

  const active = computed(() => tiptap.value[focusId.value]);
  const lastActive = computed(() => tiptap.value[lastFocusId.value]);

  const addTiptap = (id, newTiptap) => {
    tiptap.value[id] = newTiptap;
  };

  const updateFocus = (id) => {
    focusId.value = id;
    if (id) {
      lastFocusId.value = id;
    }
  };

  const removeTiptap = (id) => {
    tiptap.value[id] = null;
    if (focusId.value === id) {
      updateFocus('');
    }
  };

  const updateDocumentFromAts = ({ fields, ...data }) => {
    Object.keys(data).forEach((id) => {
      if (!tiptap.value[id]) {
        throw new Error(`Editor not found: ${id}`);
      }
      tiptap.value[id].chain()
        .setMeta('addToHistory', false)
        .setContent(data[id], {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: true },
        })
        .run();
    });

    Object.keys(fields).forEach((id) => {
      if (!tiptap.value[id]) {
        throw new Error(`Field not found: ${id}`);
      }
      tiptap.value[id].chain()
        .setMeta('addToHistory', false)
        .setContent(fields[id]?.html || '', {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: true },
        })
        .run();
    });
  };

  const updateDocumentFromTemplate = (template) => {
    ['title', 'company', 'location', 'flexibleId'].forEach((id) => {
      tiptap.value[id].chain()
        .setMeta('addToHistory', false)
        .setContent(template[id], {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: true },
        })
        .run();
    });

    const templateFields = joinFooterAndHeaderFields(template.description_json.fields)
      .filter((field) => !shouldHideIfEmpty({
        type: field.type, sections: field.sections, isTemplate: false,
      }));

    templateFields.forEach((field) => {
      let fieldHtml = '';
      field.sections.forEach(({ blocks }) => {
        blocks.forEach(({ html }) => {
          fieldHtml += html;
        });
      });

      const editor = tiptap.value[field.type];
      if (editor) {
        editor.chain()
          .setMeta('addToHistory', false)
          .setContent(fieldHtml, {
            emitUpdate: true,
            parseOptions: { preserveWhitespace: true },
          })
          .run();
      }
    });
  };

  const selectedJson = ref([]);
  const selectedHtml = ref('');

  const updateSelectedJson = ({ blur = false, extensions } = {}) => {
    if (blur) {
      selectedJson.value = [];
      selectedHtml.value = '';
      return;
    }

    if (!active.value) {
      selectedJson.value = [];
      selectedHtml.value = '';
      return;
    }

    const { view, state } = active.value;
    const { from, to } = view.state.selection;

    if (from === to) {
      selectedJson.value = [];
      selectedHtml.value = '';
      return;
    }

    const selectionNodes = [];
    const nodeToNodeCopy = new WeakMap();

    state.doc.nodesBetween(from, to, (node, pos, parent) => {
      const nodeCopy = JSON.parse(JSON.stringify(node));
      nodeCopy.content = [];
      nodeToNodeCopy.set(node, nodeCopy);

      if (parent === state.doc) {
        selectionNodes.push(nodeCopy);
      } else {
        const parentCopy = nodeToNodeCopy.get(parent);
        parentCopy.content.push(nodeCopy);
      }

      if (nodeCopy.type === 'text') {
        nodeCopy.text = nodeCopy.text.substring(from - pos, to - pos);
      }
    });

    selectedJson.value = selectionNodes;
    selectedHtml.value = generateHTML({ type: 'doc', content: selectionNodes }, extensions);
  };

  return {
    tiptap,
    focusId,
    lastFocusId,
    active,
    lastActive,
    selectedJson,
    selectedHtml,
    addTiptap,
    removeTiptap,
    updateFocus,
    updateDocumentFromAts,
    updateDocumentFromTemplate,
    updateSelectedJson,
  };
};

const documentDataModule = () => {
  const fieldsOriginal = ref([]);
  const title = ref('');
  const location = ref('');
  const company = ref('');
  const flexibleId = ref('');

  const updateDocumentData = (data) => {
    fieldsOriginal.value = data.fields;
    title.value = data.title;
    location.value = data.location;
    company.value = data.company;
    flexibleId.value = data.flexibleId;
  };

  const updateTitle = (newTitle) => {
    title.value = newTitle;
  };

  const updateLocation = (newLocation) => {
    location.value = newLocation;
  };

  const updateCompany = (newCompany) => {
    company.value = newCompany;
  };

  const updateFlexibleId = (newFlexibleId) => {
    flexibleId.value = newFlexibleId;
  };

  const updateFields = (fields) => {
    fieldsOriginal.value = fields;
  };

  const getDefaultBlock = (field) => {
    const noParagraph = field.formatBlacklist.includes('P');
    return {
      id: uuidv4(),
      type: noParagraph ? 'header' : 'paragraph',
      lockType: '',
      formatBlacklist: [],
      html: noParagraph ? '<h3></h3>' : '<p></p>',
    };
  };

  const getDefaultSection = (field) => ({
    id: uuidv4(),
    type: 'default',
    lockType: '',
    formatBlacklist: [],
    blocks: [getDefaultBlock(field)],
  });

  // add html to each field
  const fields = computed(() => fieldsOriginal.value.map((field) => {
    let html = '';
    let isLocked = true;
    const isLockable = isFieldLockable(field.type);
    const isHeaderOrFooter = isHeaderOrFooterField(field.type);

    const { sections } = field;
    if (!sections?.length) {
      sections.push(getDefaultSection(field));
    }

    sections.forEach((section) => {
      const { blocks } = section;
      if (!blocks?.length) {
        blocks.push(getDefaultBlock(field));
      }

      blocks.forEach((block) => {
        if (!block.lockType) {
          isLocked = false;
        }
        html += block.html;
      });
    });

    // reduce ['H1', 'H2', 'P', 'OL'] to { H1: true, H2: true, P: true, OL: true }
    const nodes = field.formatBlacklist.reduce((res, tag) => ({ ...res, [tag]: true }), {});

    return {
      ...field,
      html,
      isLocked: isLockable && isLocked,
      isLockable,
      isHeaderOrFooter,

      disabledNodes: {
        paragraph: nodes.P,
        heading: nodes.H1 && nodes.H2 && nodes.H3,
        h1: nodes.H1,
        h2: nodes.H2,
        h3: nodes.H3,
        list: nodes.UL,
        orderedList: nodes.OL,
        link: nodes.A,
        bold: nodes.STRONG,
        italic: nodes.EM,
      },
    };
  }));

  const toggleLock = (fieldIndex) => {
    const fieldOriginal = fieldsOriginal.value[fieldIndex];
    const { isLocked } = fields.value[fieldIndex];

    if (!fieldOriginal) {
      return;
    }

    fieldOriginal.sections?.forEach((section) => {
      section.blocks?.forEach((block) => {
        // eslint-disable-next-line no-param-reassign
        block.lockType = isLocked ? false : 'content';
      });
    });
  };

  /**
   * Add the side bar suggestion to the last unlocked field's last block
   * @param suggestion
   */
  const oneClickAddSuggestion = (suggestion) => {
    const { addErrorSnackbar } = useAppSnackbar();

    const lastFieldIndex = fields.value
      .findLastIndex(({
        isLocked,
        sections,
        isHeaderOrFooter,
      }) => !isLocked && !isHeaderOrFooter && sections.some(({ lockType }) => lockType !== 'content'));

    const lastField = fields.value[lastFieldIndex];

    if (!lastField) {
      addErrorSnackbar({ text: 'No unlocked fields found to place suggestion' });
      return;
    }

    const html = lastField.disabledNodes.paragraph ? `<h3>${suggestion}</h3>` : `<p>${suggestion}</p>`;

    fieldsOriginal.value = fieldsOriginal.value.map((field, index) => {
      if (index === lastFieldIndex) {
        const newField = { ...field };

        const lastSectionIndex = newField.sections
          .findLastIndex((section) => section.lockType !== 'content');

        const lastSectionLastBlockIndex = newField.sections[lastSectionIndex].blocks
          .findLastIndex((block) => block.lockType !== 'content');

        newField.sections[lastSectionIndex].blocks[lastSectionLastBlockIndex].html += html;

        return newField;
      }

      return field;
    });
  };

  return {
    title,
    location,
    company,
    flexibleId,
    fields,
    fieldsOriginal,
    updateDocumentData,
    updateTitle,
    updateCompany,
    updateLocation,
    updateFlexibleId,
    updateFields,
    toggleLock,
    oneClickAddSuggestion,
  };
};

export const useDocumentEditor = defineStore('documentEditor', () => ({
  ...tiptapFocusModule(), ...documentDataModule(),
}));
